import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LandingPageModule } from './landing-page/landing-page.module';
import { NotFoundComponent } from './app-pages/not-found/not-found.component';
import { TermsOfServiceComponent } from './app-pages/legal/terms-of-service/terms-of-service.component';
import { PrivacyPolicyComponent } from './app-pages/legal/privacy-policy/privacy-policy.component';
import { AppPickerComponent } from './app-pages/app-picker/app-picker.component';
import { AppLoadingComponent } from './app-pages/app-loading/app-loading.component';
import { QuickBooksCallbackComponent } from './app-pages/quickbooks-callback/quickbooks-callback.component';

import { BoardModule } from './board/board.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';

import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
// MatSnackBar is providedIn: MatSnackBarModule, so it only exists in injectors
// that import it. AdminModule does, but it's lazy — root-declared components
// like the QuickBooks callback need it here or they fail on a cold load.
import { MatSnackBarModule } from '@angular/material/snack-bar';

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';

import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';

import { environment } from 'src/environments/environment';
import { BhubTopBarModule } from './bhub-top-bar/bhub-top-bar.module';
import { AppEffects } from './store/app-state.effects';
import { appReducer } from './store/app-state.reducer';
import { AppHttpInterceptor } from './common/global-interceptors/appid-interceptor';
import { RetryHttpInterceptor } from './common/global-interceptors/retry-interceptor';

// PrimeNG
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { RequestHeaderInterceptor } from './common/global-interceptors/request-header-interceptor';

export function loggerCallback(logLevel: LogLevel, message: string) {
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      redirectUri: environment.msalConfig.auth.redirectUri,
      postLogoutRedirectUri: environment.msalConfig.auth.redirectUri,
      knownAuthorities: [environment.b2cPolicies.authorityDomain],
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(
    environment.apiConfig.baseUri,
    environment.apiConfig.scopes
  );

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

/**
 * The root module of the Business Hub application.
 */
@NgModule({
    declarations: [
        AppComponent,
        NotFoundComponent,
        TermsOfServiceComponent,
        PrivacyPolicyComponent,
        NotFoundComponent,
        AppPickerComponent,
        AppLoadingComponent,
        QuickBooksCallbackComponent
    ],
    bootstrap: [
      AppComponent,
      MsalRedirectComponent
    ],
    imports: [
      BhubTopBarModule,
      LandingPageModule,
      MatIconModule,
      MatSidenavModule,
      MatButtonModule,
      MatMenuModule,
      MatDividerModule,
      MatToolbarModule,
      MatTabsModule,
      MatProgressSpinnerModule,
      MatSnackBarModule,
      BrowserModule,
      AppRoutingModule,
      FormsModule,
      ReactiveFormsModule,
      BoardModule,
      BrowserAnimationsModule,
      MsalModule,
      StoreModule.forRoot({ app: appReducer }),
      EffectsModule.forRoot([AppEffects])
    ],
    providers: [

      {
        provide: MSAL_INSTANCE,
        useFactory: MSALInstanceFactory,
      },
      {
        provide: MSAL_GUARD_CONFIG,
        useFactory: MSALGuardConfigFactory,
      },
      {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: MSALInterceptorConfigFactory,
      },
      MsalService,
      MsalGuard,
      MsalBroadcastService,
      provideAnimationsAsync(),
        providePrimeNG({
            theme: {
                preset: Aura
            }
        }),
      provideHttpClient(withInterceptorsFromDi()),
      {  provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true, },
      {  provide: HTTP_INTERCEPTORS, useClass: RequestHeaderInterceptor, multi: true },
      {  provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
      {  provide: HTTP_INTERCEPTORS, useClass: RetryHttpInterceptor, multi: true }, // Register the RetryHttpInterceptor
    ]
  })

export class AppModule { }
